import { ArrowDownRight, ArrowUpRight, MoreHorizontal } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const toneStyles = {
  primary: {
    icon: "bg-brand-50 text-brand-500",
    badge: "bg-brand-50 text-brand-500",
  },
  success: {
    icon: "bg-[#ecfdf3] text-[#039855]",
    badge: "bg-[#ecfdf3] text-[#039855]",
  },
  warning: {
    icon: "bg-[#fffaeb] text-[#dc6803]",
    badge: "bg-[#fffaeb] text-[#dc6803]",
  },
  danger: {
    icon: "bg-[#fef3f2] text-[#d92d20]",
    badge: "bg-[#fef3f2] text-[#d92d20]",
  },
  neutral: {
    icon: "bg-gray-100 text-gray-600",
    badge: "bg-gray-100 text-gray-600",
  },
};

export function TailAdminPageHeader({ eyebrow, title, description, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        {eyebrow ? (
          <p className="mb-2 text-xs font-medium uppercase leading-5 text-brand-500">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="text-2xl font-semibold text-gray-900 md:text-3xl">
          {title}
        </h1>
        {description ? (
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-3">{actions}</div> : null}
    </div>
  );
}

export function TailAdminMetricCard({
  label,
  value,
  icon: Icon,
  tone = "primary",
  delta,
  helper = "Live metrics",
  loading,
}) {
  const style = toneStyles[tone] || toneStyles.primary;
  const positive = !String(delta || "").startsWith("-");

  return (
    <section className="group rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs transition hover:-translate-y-0.5 hover:shadow-theme-sm">
      <div className="mb-5 flex items-center justify-between">
        <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl", style.icon)}>
          {Icon ? <Icon className="h-6 w-6 tailadmin-icon" /> : null}
        </div>
        {delta ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
              positive ? toneStyles.success.badge : toneStyles.danger.badge
            )}
          >
            {positive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
            {delta}
          </span>
        ) : null}
      </div>
      <p className="text-sm text-gray-500">{label}</p>
      {loading ? (
        <Skeleton className="mt-2 h-8 w-28" />
      ) : (
        <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
      )}
      <div className="mt-4 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-[#12b76a]" />
        <span className="text-xs font-medium uppercase text-gray-400">{helper}</span>
      </div>
    </section>
  );
}

export function TailAdminPanel({ title, description, action, children, className }) {
  return (
    <section className={cn("rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs", className)}>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
        </div>
        {action ? (
          action
        ) : (
          <button className="rounded-lg p-2 text-gray-400 hover:bg-gray-50 hover:text-gray-600">
            <MoreHorizontal className="h-5 w-5" />
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

export function TailAdminStatusBadge({ children, tone = "neutral" }) {
  const style = toneStyles[tone] || toneStyles.neutral;
  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold", style.badge)}>
      {children}
    </span>
  );
}

export function TailAdminDataTable({ columns, rows, loading }) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((item) => (
          <Skeleton key={item} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-full overflow-x-auto">
      <table className="min-w-full">
        <thead className="border-y border-gray-100">
          <tr>
            {columns.map((column) => (
              <th key={column} className="py-3 text-left text-xs font-medium text-gray-500">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, rowIndex) => (
            <tr key={row.id || rowIndex}>
              {row.cells.map((cell, cellIndex) => (
                <td key={`${rowIndex}-${cellIndex}`} className="py-3 pr-4 text-sm text-gray-500">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TailAdminProgressRow({ label, value, helper, tone = "primary" }) {
  const style = toneStyles[tone] || toneStyles.primary;
  return (
    <div className="rounded-xl border border-gray-100 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-700">{label}</p>
          {helper ? <p className="text-xs text-gray-500">{helper}</p> : null}
        </div>
        <span className={cn("rounded-full px-2.5 py-1 text-xs font-semibold", style.badge)}>{value}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-brand-500" style={{ width: `${Math.max(0, Math.min(100, Number(value) || 0))}%` }} />
      </div>
    </div>
  );
}

export function TailAdminEmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex min-h-[260px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 p-8 text-center">
      {Icon ? (
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
          <Icon className="h-7 w-7 tailadmin-icon" />
        </div>
      ) : null}
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      {description ? <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">{description}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function TailAdminCodeBlock({ children, className }) {
  return (
    <pre className={cn("overflow-x-auto rounded-xl bg-gray-900 p-4 text-sm leading-6 text-white shadow-theme-xs", className)}>
      <code>{children}</code>
    </pre>
  );
}

export function TailAdminFormSection({ icon: Icon, title, description, children, className }) {
  return (
    <section className={cn("rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs", className)}>
      <div className="mb-5 flex items-start gap-3">
        {Icon ? (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-500">
            <Icon className="h-5 w-5 tailadmin-icon" />
          </div>
        ) : null}
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

export function TailAdminDetailRow({ label, value, helper, action }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-100 p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {helper ? <p className="mt-0.5 text-xs text-gray-500">{helper}</p> : null}
      </div>
      <div className="flex shrink-0 items-center gap-3 text-sm font-medium text-gray-900">
        {value}
        {action}
      </div>
    </div>
  );
}
